import { useQuery } from "@tanstack/react-query";
import { StripeTokenResponse } from "components/StripePaymentForm/StripePaymentForm";
import { Dayjs } from "dayjs";
import {
  AccountDetails,
  AccountWithTicketsDetails,
  BillingInterval,
  InvoiceDetails,
  RemainingChargesDetails,
  SignupChargeDetails,
  SubscriptionGiftDetails,
  SubscriptionSource,
  UserFields,
} from "features/types";
import qs from "qs";
import { get, patch, post, put } from "services/http";
import { SubscriptionChange } from "services/types";

export interface UpdateAccountRequest {
  seatPreference: "NONE" | "INCLUDE_ACCESSIBLE" | "ONLY_ACCESSIBLE";
  marketplace: boolean;
  emailMarketingConsent: boolean;
  smsMarketingConsent: boolean;
}

export interface UpdateSubscriptionRequest {
  accountId: number;
  subscriptionId: number;
  expiresAt?: string;
  revShareEnabled?: boolean;
  autoRenew?: boolean;
  // Required when enabling auto-renew on a plan with requireAutoRenewalConsent
  autoRenewalAgreementId?: number;
}

interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  userFields?: UserFields;
}

interface CreateSubscriptionGiftRequest {
  toUser: CreateUserRequest;
  planPackageId: number;
  quantity: number;
  deliverAt: string | Dayjs;
  source: SubscriptionSource;
}

interface UpdateGiftRequest {
  code: string;
  toUser: {
    firstName: string;
    lastName: string;
    email: string;
  };
  deliverAt: Dayjs;
}
interface AccountPrimaryUserRequest {
  userId: number;
  planPackageIds: number[];
}

export interface AccountMemberRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  minor: string;
  userFields: UserFields;
  type: "PRIMARY" | "SECONDARY" | "ADDON" | "REMOVED";
  planPackageIds: number[];
}

interface ClaimSubscriptionGiftRequest {
  code: string;
  primaryUser: AccountPrimaryUserRequest;
  members?: AccountMemberRequest[];
  source: SubscriptionSource;
}

interface FetchSignupChargeRequest {
  billingInterval: BillingInterval;
  planPackageIds: number[];
  prorationDate?: string | Dayjs;
}

interface UpdateAccountMemberRequest {
  accountId: number;
  memberId: number;
  extUsername: string;
  eventId: number;
  playerId?: string;
}

export const ACCOUNT_QUERY_KEYS = {
  fetchAccountBff: "fetchAccountBFF",
};

export const useFetchAccountBFFQuery = () => {
  return useQuery(
    [ACCOUNT_QUERY_KEYS.fetchAccountBff],
    () =>
      get({
        url: "/bff/subscribers/account_with_tickets",
      }) as Promise<AccountWithTicketsDetails>,
    {
      staleTime: Infinity,
      enabled: false,
    },
  );
};

export const useFetchRemainingChargesQuery = ({
  accountId,
  planPackageId,
}: {
  accountId: number | undefined;
  planPackageId: number | string;
}) => {
  return useQuery(
    ["fetchRemainingCharges"],
    () =>
      get({
        url: `/accounts/${accountId}/remaining_charges/${planPackageId}`,
      }) as Promise<RemainingChargesDetails>,
  );
};

export const fetchSignupChargeRequest = (params: FetchSignupChargeRequest) => {
  return get({
    url: `/accounts/signup_charge`,
    params,
    paramsSerializer: (params: FetchSignupChargeRequest) =>
      qs.stringify(params),
  }) as Promise<SignupChargeDetails>;
};

export const useFetchInvoices = () => {
  return useQuery(
    ["fetchInvoices"],
    () =>
      get({
        url: `/accounts/invoices`,
      }) as Promise<Array<InvoiceDetails>>,
  );
};

export const updateAccountMemberRequest = ({
  accountId,
  memberId,
  extUsername,
  eventId,
  playerId,
}: UpdateAccountMemberRequest) => {
  return patch({
    url: `accounts/${accountId}/members/${memberId}`,
    params: {
      extUsername,
      eventId,
      playerId,
    },
  }) as Promise<AccountDetails>;
};

export const addSubscriptionFetcher = ({
  billingInterval,
  userInfo,
  accountId,
  source,
  autoRenewalAgreementId,
}: {
  billingInterval: BillingInterval;
  userInfo: {
    userId: number;
    planPackageIds: number[];
  }[];
  accountId: number;
  source: SubscriptionSource;
  autoRenewalAgreementId?: number;
}) => {
  return post({
    method: "post",
    url: `/accounts/${accountId}/subscriptions`,
    params: {
      billingInterval,
      subscriptions: userInfo,
      source,
      autoRenewalAgreementId,
    },
  }) as Promise<AccountDetails>;
};

// One new member for POST /accounts/{id}/members (AccountMemberRequest).
// Email may be null only for minors; address fields are optional.
export interface AddAccountMemberInput {
  email: string | null;
  firstName: string;
  lastName: string;
  imageIdentifier: string;
  phone: string;
  minor: boolean;
  userFields: UserFields;
  type: "SECONDARY";
  planPackageIds: number[];
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export const addMembersFetcher = ({
  billingInterval,
  members,
  accountId,
  source,
}: {
  billingInterval: BillingInterval;
  members: AddAccountMemberInput[];
  accountId: number;
  source: SubscriptionSource;
}) => {
  return post({
    url: `/accounts/${accountId}/members`,
    params: {
      billingInterval,
      members,
      source,
    },
    method: "POST",
  }) as Promise<AccountDetails>;
};

export const updateBillingFetcher = ({
  accountId,
  token,
}: {
  accountId: number;
  token: StripeTokenResponse;
}) => {
  const {
    token: { id },
    nameOnCard,
    postalCode,
  } = token;
  return patch({
    url: `/accounts/${accountId}/update_billing`,
    params: {
      billingToken: id,
      billingNameOnCard: nameOnCard,
      billingAddress: "",
      billingAddressLine2: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: postalCode,
      billingCountry: "",
    },
  }) as Promise<AccountDetails>;
};

export const changeBillingIntervalFetcher = ({
  accountId,
  planPackageId,
  billingInterval = "FULL_SEASON",
}: {
  accountId: number;
  planPackageId: number;
  billingInterval?: BillingInterval;
}) => {
  return patch({
    url: `/accounts/${accountId}/change_billing_interval`,
    params: {
      billingInterval,
      planPackageId,
    },
  }) as Promise<AccountDetails>;
};

export const updateSubscriptionFetcher = ({
  accountId,
  subscriptionId,
  ...params
}: UpdateSubscriptionRequest) => {
  return patch({
    url: `/accounts/${accountId}/subscriptions/${subscriptionId}`,
    params,
  }) as Promise<AccountDetails>;
};

export const cancelOrReinstateSubscriptionFetcher = ({
  accountId,
  subscriptionId,
  type,
}: {
  accountId: number;
  subscriptionId: number;
  type: "cancel" | "reinstate";
}) => {
  return patch({
    url: `/accounts/${accountId}/subscriptions/${subscriptionId}/${type}`,
  }) as Promise<AccountDetails>;
};

export const getMemberBarcodeFetcher = (memberId: number) => {
  return get({
    url: `/accounts/members/${memberId}/barcode`,
  });
};

export const updateAccountFetcher = (params: UpdateAccountRequest) => {
  return patch({
    url: "/accounts",
    params,
  }) as Promise<AccountDetails>;
};

export const getPlanChangeChargeFetcher = ({
  accountId,
  changes,
  source,
}: {
  accountId: number;
  changes: SubscriptionChange[];
  source: SubscriptionSource;
}) => {
  return post({
    url: `/accounts/${accountId}/plan_change_charge`,
    params: {
      changes,
      source,
    },
  }) as Promise<SignupChargeDetails>;
};

export const changeSubscriptionsFetcher = ({
  accountId,
  changes,
  source,
}: {
  accountId: number;
  changes: SubscriptionChange[];
  source: SubscriptionSource;
}) => {
  return patch({
    url: `/accounts/${accountId}/subscriptions/change`,
    params: {
      changes,
      source,
    },
  }) as Promise<AccountDetails>;
};

export const purchaseGiftsRequest = (params: CreateSubscriptionGiftRequest) => {
  return post({
    url: "/accounts/gifts",
    params,
  }) as Promise<AccountDetails>;
};

export interface RenewalOptInDetails {
  planPackageName: string;
  alreadyRedeemed: boolean;
  // Rendered auto-renewal disclosure terms to show with the consent checkbox;
  // null when the plan does not require consent, and on redeem responses.
  termsBody: string | null;
}

// Public endpoints behind the opt-in link in renewal opt-in notice emails.
// The GET shows the confirmation step; the POST performs the opt-in.
export const fetchRenewalOptInDetailsFetcher = (token: string) => {
  return get({
    url: `/accounts/renewal_opt_in/${token}`,
  }) as Promise<RenewalOptInDetails>;
};

export const redeemRenewalOptInFetcher = (token: string) => {
  return post({
    url: "/accounts/renewal_opt_in",
    params: { token },
  }) as Promise<RenewalOptInDetails>;
};

export const useFetchGiftDetailsQuery = (code: string) => {
  return useQuery(
    ["fetchGift"],
    () =>
      get({
        url: `/accounts/gifts/${code}`,
      }) as Promise<SubscriptionGiftDetails>,
    {
      staleTime: Infinity,
    },
  );
};

export const updateGiftRequest = (params: UpdateGiftRequest) => {
  return put({
    url: `/accounts/gifts/${params.code}`,
    params,
  });
};

export const claimGiftRequest = (params: ClaimSubscriptionGiftRequest) => {
  return patch({
    url: `/accounts/gifts/${params.code}`,
    params,
  }) as Promise<AccountDetails>;
};

export const useFetchGiftHistoryQuery = () => {
  return useQuery(
    ["fetchGiftHistory"],
    () => {
      return get({
        url: "/accounts/gifts",
      }) as Promise<SubscriptionGiftDetails[]>;
    },
    {
      staleTime: Infinity,
    },
  );
};
